import * as Yup from 'yup';
import { parseDecimal } from './helpers';

// PER-100 fields: at most 3 integer digits (physical sufit — macros ≤100, energy ≤900 per 100),
// optional decimal part. Rejects "1000", "1234.5", etc.
const DECIMAL_REGEX_3 = /^\d{1,3}([.,]\d+)?$/;
// PER-SERVING fields: a serving can be up to 999 units, so a per-serving value legitimately
// reaches 4 integer digits (e.g. ~900 kcal * 999/100). Cap integer digits at 4; the real bound
// is the relative-to-serving check below.
const DECIMAL_REGEX_4 = /^\d{1,4}([.,]\d+)?$/;

const INVALID = 'validation.invalidNumber';
// Used where the bound is dynamic (PER_SERVING scales with the serving size), so no fixed range can
// be quoted — but "enter a valid number" is misleading for a well-formed value that is merely out
// of range, e.g. 500 kcal declared for a 30 g serving.
const TOO_HIGH = 'validation.valueTooHigh';

// PER-100 fields state their actual bound (Figma 9750:49931), e.g. "Value must be between 0 and
// 900". PER-SERVING fields keep the generic message on purpose: their bound scales with the serving
// size, so no fixed range copy would be true.
const PER_100_RANGE_MESSAGE = {
  calories: 'tracker.validationError.accetableValue.calories',
  protein: 'tracker.validationError.accetableValue.proteins',
  fat: 'tracker.validationError.accetableValue.fat',
  carbs: 'tracker.validationError.accetableValue.carbs',
  fiber: 'tracker.validationError.accetableValue.fiber',
} as const;

// Absolute per-100-units ceilings (physical maxima): a macro can't exceed 100 g per 100 g/ml,
// and energy can't realistically exceed ~900 kcal per 100 (pure fat ≈ 900).
const MAX_MACRO_PER_100 = 100;
const MAX_CALORIES_PER_100 = 900;

const matchesFormat = (regex: RegExp) => (value: string | null | undefined) =>
  value == null || value === '' ? true : regex.test(value);

// A macro/calorie field for the PER_100 set: 3-digit format + a fixed upper bound. Both the format
// and the bound failure report the field's range ("between 0 and 900"), since an over-long or
// non-numeric entry is out of range too — a generic "invalid number" left the user guessing.
const per100Field = (maxValue: number, required: boolean, rangeMessage: string) => {
  let schema = Yup.string()
    .test('decimal-format', rangeMessage, matchesFormat(DECIMAL_REGEX_3))
    .test('max-value', rangeMessage, (value) =>
      value == null || value === '' ? true : parseDecimal(value) <= maxValue
    );
  if (required) schema = schema.required('validation.required');
  return schema;
};

// A macro/calorie field for the PER_SERVING set: 4-digit format + a bound relative to the serving
// size (`perFactor` = ceiling per 1 unit of serving, e.g. 1 for macros, 9 for calories). Reads the
// sibling `servingSize` off the root form via Yup's `this.from` ancestor chain (last entry is the
// whole form). Empty/invalid serving size skips the bound — the servingSize field surfaces its
// own error then. No Yup context wiring required.
const perServingField = (perFactor: number, required: boolean) => {
  let schema = Yup.string()
    .test('decimal-format', INVALID, matchesFormat(DECIMAL_REGEX_4))
    .test('max-vs-serving', TOO_HIGH, function (value) {
      if (value == null || value === '') return true;
      const root = this.from?.[this.from.length - 1]?.value as { servingSize?: string } | undefined;
      const serving = root?.servingSize ? parseDecimal(root.servingSize) : NaN;
      if (!Number.isFinite(serving) || serving <= 0) return true; // serving invalid → own error
      return parseDecimal(value) <= perFactor * serving;
    });
  if (required) schema = schema.required('validation.required');
  return schema;
};

const per100Macros = (required: boolean) =>
  Yup.object().shape({
    calories: per100Field(MAX_CALORIES_PER_100, required, PER_100_RANGE_MESSAGE.calories),
    protein: per100Field(MAX_MACRO_PER_100, required, PER_100_RANGE_MESSAGE.protein),
    fat: per100Field(MAX_MACRO_PER_100, required, PER_100_RANGE_MESSAGE.fat),
    carbs: per100Field(MAX_MACRO_PER_100, required, PER_100_RANGE_MESSAGE.carbs),
    fiber: per100Field(MAX_MACRO_PER_100, false, PER_100_RANGE_MESSAGE.fiber),
  });

const perServingMacros = (required: boolean) =>
  Yup.object().shape({
    calories: perServingField(MAX_CALORIES_PER_100 / 100, required),
    protein: perServingField(1, required),
    fat: perServingField(1, required),
    carbs: perServingField(1, required),
    fiber: perServingField(1, false),
  });

export default Yup.object().shape({
  name: Yup.string()
    .trim()
    .required('validation.required')
    .max(80, 'tracker.addIngredient.productName.edit.limitReached'),
  brand: Yup.string().max(50, 'tracker.addIngredient.brandName.edit.limitReached'),
  unit: Yup.string().oneOf(['GRAMS', 'MILLILITERS'], 'validation.required'),
  mode: Yup.string().oneOf(['PER_100_UNITS', 'PER_SERVING']).required(),
  servingSize: Yup.string().when('mode', {
    is: 'PER_SERVING',
    then: (schema) =>
      schema
        .required('validation.required')
        .matches(DECIMAL_REGEX_3, INVALID)
        .test('positive', INVALID, (value) => (value ? parseDecimal(value) > 0 : false)),
    otherwise: (schema) =>
      schema.notRequired().matches(DECIMAL_REGEX_3, { message: INVALID, excludeEmptyString: true }),
  }),
  // Only the active mode's set is required; the inactive set is format-checked only, so a
  // previously prefilled value there can't be malformed but empty is fine.
  per100: Yup.object().when('mode', {
    is: 'PER_100_UNITS',
    then: () => per100Macros(true),
    otherwise: () => per100Macros(false),
  }),
  perServing: Yup.object().when('mode', {
    is: 'PER_SERVING',
    then: () => perServingMacros(true),
    otherwise: () => perServingMacros(false),
  }),
});
